import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Copy, ChevronDown, History, ShieldCheck } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEBICHECK_CLASS = {
  submitted: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

const STATUS_CLASS = {
  approved: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  under_review: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-slate-100 text-slate-600',
  pending: 'bg-amber-100 text-amber-700',
  submitted: 'bg-blue-100 text-blue-700',
};

export default function LoanHistoryPopover({ app, getAuthHeader }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (open) => {
    if (!open || history) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/customers/${app.user_id}/applications`, getAuthHeader());
      setHistory(res.data);
    } catch { toast.error('Failed to load loan history'); }
    setLoading(false);
  };

  const copyRef = (ref) => {
    navigator.clipboard.writeText(ref).then(() => toast.success(`Reference ${ref} copied`)).catch(() => toast.error('Copy failed'));
  };

  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-0.5 rounded-full border border-purple-200 bg-purple-100 text-purple-700 text-[10px] font-semibold py-0 px-1.5 hover:bg-purple-200 transition-colors cursor-pointer"
          data-testid={`repeat-badge-${app.id}`}
          onClick={e => e.stopPropagation()}
        >
          Repeat x{app.total_applications}
          <ChevronDown className="w-2.5 h-2.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start" data-testid="loan-history-popover">
        <div className="px-3 py-2.5 border-b bg-slate-50 rounded-t-md">
          <p className="text-sm font-semibold flex items-center gap-1.5"><History className="w-3.5 h-3.5" />Loan History — {app.full_name}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Each loan has its own reference. For DebiCheck, use the reference of the loan being debited.</p>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y">
          {loading && <p className="text-xs text-slate-500 px-3 py-3">Loading…</p>}
          {history && history.length === 0 && <p className="text-xs text-slate-500 px-3 py-3">No applications found</p>}
          {history && history.map(h => (
            <div key={h.id} className={`px-3 py-2 flex items-center gap-2 ${h.id === app.id ? 'bg-blue-50' : ''}`} data-testid={`loan-history-row-${h.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs font-semibold">{h.reference}</span>
                  <button className="text-slate-400 hover:text-slate-700" onClick={() => copyRef(h.reference)} title="Copy reference" data-testid={`copy-ref-${h.id}`}>
                    <Copy className="w-3 h-3" />
                  </button>
                  {h.id === app.id && <span className="text-[9px] font-bold text-blue-600 uppercase">This row</span>}
                </div>
                <p className="text-[11px] text-slate-500">
                  R{(h.loan_amount || 0).toLocaleString()} loan · R{(h.total_repayment || 0).toLocaleString()} repay · {h.created_at ? new Date(h.created_at).toLocaleDateString('en-ZA') : ''}
                </p>
                {h.debicheck_status && h.debicheck_status !== 'none' && (
                  <p className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1 mt-0.5 ${DEBICHECK_CLASS[h.debicheck_status] || 'bg-slate-100 text-slate-600'}`} data-testid={`history-debicheck-${h.id}`}>
                    <ShieldCheck className="w-2.5 h-2.5" />DebiCheck {h.debicheck_status}{h.debicheck_mandate_reference ? ` · ${h.debicheck_mandate_reference}` : ''}
                  </p>
                )}
              </div>
              <Badge variant="outline" className={`text-[10px] py-0 ${STATUS_CLASS[h.status] || 'bg-slate-100 text-slate-600'}`}>{h.status}</Badge>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
